/**
 * Invoice-PDF ingest page — drop a tax-invoice PDF, review parsed data,
 * post as Sales / Purchase Invoice.
 *
 * Backend gap: `POST /api/invoice-ingest/parse` (§36.1, G48) does not
 * exist yet. Until it does, the FE falls back to a client-side stub
 * parser (`stubParseByFilename`) that returns canned data matching the
 * two reference PDFs in `/doc/sample-invoices/`. The Post-Invoice flow
 * is real — it calls live SalesInvoice / PurchaseInvoice /
 * InventoryMovement endpoints + auto-creates missing Products in the
 * master.
 *
 * Layout:
 *   ┌─ toolbar ─────────────────────────────────────────────────┐
 *   │ ← Back  | drop-zone .................... [Parse]  Sales|Purchase │
 *   ├────────────────────────────┬──────────────────────────────┤
 *   │ Parsed data (editable)     │ Tax-invoice-style preview    │
 *   │  header                    │   header                     │
 *   │  lines (Sr Desc HSN Qty …) │   lines                      │
 *   │  totals                    │   totals + bank              │
 *   └────────────────────────────┴──────────────────────────────┘
 *                                 [ Post Invoice → live API ]
 */

import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, useToast } from '@kss/ui';
import {
  ArrowLeft, FileText, Loader2, Save, Upload, Plus, Trash2,
} from 'lucide-react';
import {
  productService,
  salesInvoiceService,
  salesInvoiceLineService,
  purchaseInvoiceService,
  purchaseInvoiceLineService,
  inventoryMovementService,
} from '@kss/api';

/* -------------------------------------------------------------------- */
/*  Types — mirror the §36.1 response shape                             */
/* -------------------------------------------------------------------- */

interface ParsedHeader {
  kindGuess: 'SalesInvoice' | 'PurchaseInvoice' | 'SalesOrder';
  docNumber: string;
  docDate: string;            // yyyy-MM-dd
  dueDate: string | null;
  partyName: string;
  partyGstin: string;
  partyState: string;
  partyStateCode: string;
  partyAddress: string;
  partyEmail: string;
  partyPhone: string;
  partyContact: string;
  transportName: string;
  ewayBillNo: string;
  irn: string;
}

interface ParsedLine {
  srNo: number;
  description: string;
  hsnCode: string;
  qty: number;
  unit: string;
  packCount: number;
  packType: string;
  rate: number;
  tradeDiscount: number;
  taxableValue: number;
}

interface ParsedTotals {
  taxableValue: number;
  cgst: number;
  sgst: number;
  igst: number;
  freight: number;
  roundOff: number;
  grandTotal: number;
  amountInWords: string;
}

interface ParsedInvoice {
  header: ParsedHeader;
  lines: ParsedLine[];
  totals: ParsedTotals;
}

/* -------------------------------------------------------------------- */
/*  Stub parser — canned data while G48 is pending                      */
/* -------------------------------------------------------------------- */

function stubParseByFilename(fileName: string): ParsedInvoice {
  const lower = fileName.toLowerCase();

  // BHW domestic sales order (Arabian Petroleum → Dhakad Lubricant)
  if (lower.includes('domestic') || lower.includes('bhw') || lower.includes('00117')) {
    return {
      header: {
        kindGuess: 'SalesOrder',
        docNumber: 'BHW/SO/26-27/00117',
        docDate: '2026-05-04',
        dueDate: null,
        partyName: 'DHAKAD LUBRICANT',
        partyGstin: '08BEWPN7765J1ZE',
        partyState: 'RAJASTHAN',
        partyStateCode: '08',
        partyAddress: 'SARPANCH COLONY, NEAR BHASKAR SCHOOL, OPP KRISHI MANDI, BUS STAND ROAD, TONK, RAJASTHAN',
        partyEmail: 'vikasnagar568@gmail.com',
        partyPhone: '8740080568',
        partyContact: 'MR. VIKAS',
        transportName: 'PAID DOOR DELIVERY',
        ewayBillNo: '',
        irn: '',
      },
      lines: [
        { srNo: 1, description: 'ARZOL PACE 4T 10W/30 (API SM,JASO MA2) - (210 LTR)', hsnCode: '27101980', qty: 420, unit: 'LTR', packCount: 2, packType: 'BRL', rate: 158.30, tradeDiscount: 0, taxableValue: 66486 },
        { srNo: 2, description: 'ARZOL PACE 4T 10W/30 (API SM,JASO MA2) - (55 LTR)', hsnCode: '27101980', qty: 275, unit: 'LTR', packCount: 5, packType: 'CRB', rate: 162.30, tradeDiscount: 0, taxableValue: 44633 },
        { srNo: 3, description: 'ARZOL 4T 10W/40 (API SM,JASO MA2) - (55 LTR)', hsnCode: '27101980', qty: 220, unit: 'LTR', packCount: 4, packType: 'CRB', rate: 169.30, tradeDiscount: 0, taxableValue: 37246 },
        { srNo: 4, description: 'ARZOL DURAZOL 5W/30 (API SM,CF) - (55 LTR)', hsnCode: '27101980', qty: 55, unit: 'LTR', packCount: 1, packType: 'CRB', rate: 172.60, tradeDiscount: 0, taxableValue: 9493 },
        { srNo: 5, description: 'ARZOL UTTO - (26 LTR)', hsnCode: '27101980', qty: 234, unit: 'LTR', packCount: 9, packType: 'BKT', rate: 167.40, tradeDiscount: 0, taxableValue: 39172 },
        { srNo: 6, description: 'ARZOL GALLOP 4T 10W/30 (API SN,JASO MA2) - (900 ML) - 1MR', hsnCode: '27101980', qty: 360, unit: 'LTR', packCount: 20, packType: 'BOX', rate: 233.71, tradeDiscount: 0, taxableValue: 84136 },
        { srNo: 7, description: 'ARZOL GALLOP 4T 10W/30 (API SN,JASO MA2) - (1 LTR) - 1MR', hsnCode: '27101980', qty: 40, unit: 'LTR', packCount: 2, packType: 'BOX', rate: 232.50, tradeDiscount: 0, taxableValue: 9300 },
        { srNo: 8, description: 'ARZOL GALLOP 4T 20W/40 (API SN,JASO MA2) - (900 ML) - 1RP', hsnCode: '27101980', qty: 126, unit: 'LTR', packCount: 7, packType: 'BOX', rate: 287.00, tradeDiscount: 0, taxableValue: 36162 },
        { srNo: 9, description: 'ARZOL ZING 4T 10W/30 (API SL,JASO MA2) - (900 ML) - 1RP', hsnCode: '27101980', qty: 126, unit: 'LTR', packCount: 7, packType: 'BOX', rate: 217.00, tradeDiscount: 0, taxableValue: 27342 },
      ],
      totals: {
        taxableValue: 353969,
        cgst: 0, sgst: 0, igst: 63407.65,
        freight: 0, roundOff: 0.35,
        grandTotal: 415672,
        amountInWords: 'INR Four Lakh Fifteen Thousand Six Hundred Seventy Two Only',
      },
    };
  }

  // Default → 00132 tax invoice
  return {
    header: {
      kindGuess: 'PurchaseInvoice',
      docNumber: 'RJT/25-26/00132',
      docDate: '2025-09-30',
      dueDate: '2025-09-30',
      partyName: 'DHAKAD LUBRICANT',
      partyGstin: '08BEWPN7765J1ZE',
      partyState: 'RAJASTHAN',
      partyStateCode: '08',
      partyAddress: 'SARPANCH COLONY, NEAR BHASKAR SCHOOL, OPP KRISHI MANDI, BUS STAND ROAD, TONK, RAJASTHAN',
      partyEmail: 'vikasnagar568@gmail.com',
      partyPhone: '8740080568',
      partyContact: 'MR. VIKAS',
      transportName: 'VIJAY LAXMI ROADLINES',
      ewayBillNo: '731567772803',
      irn: '8750431d0f40a073ab2305294f428c0fc75a0bd9f482f5c7b7c0a87cfdb02150',
    },
    lines: [
      { srNo: 1, description: 'ARZOL PACE 4T 10W/30 (API SM,JASO MA2) - (55 LTR)', hsnCode: '27101980', qty: 55, unit: 'LTR', packCount: 1, packType: 'CRB', rate: 130.30, tradeDiscount: 0, taxableValue: 7166.50 },
      { srNo: 2, description: 'ARZOL PACE 4T 10W/30 (API SM,JASO MA2) - (55 LTR)', hsnCode: '27101980', qty: 110, unit: 'LTR', packCount: 2, packType: 'CRB', rate: 130.30, tradeDiscount: 0, taxableValue: 14333 },
      { srNo: 3, description: 'ARZOL PACE 4T 10W/30 (API SM,JASO MA2) - (55 LTR)', hsnCode: '27101980', qty: 210, unit: 'LTR', packCount: 1, packType: 'BRL', rate: 126.30, tradeDiscount: 0, taxableValue: 26523 },
      { srNo: 4, description: 'ARZOL PACE 4T 10W/30 (API SM,JASO MA2) - (55 LTR)', hsnCode: '27101980', qty: 165, unit: 'LTR', packCount: 3, packType: 'CRB', rate: 135.10, tradeDiscount: 0, taxableValue: 22291.50 },
      { srNo: 5, description: 'ARZOL PACE 4T 10W/30 (API SM,JASO MA2) - (210 LTR)', hsnCode: '27101980', qty: 270, unit: 'LTR', packCount: 15, packType: 'BOX', rate: 233.71, tradeDiscount: 10800, taxableValue: 63101.70 },
      { srNo: 6, description: 'ARZOL 4T 10W/40 (API SM,JASO MA2) - (55 LTR)', hsnCode: '27101980', qty: 90, unit: 'LTR', packCount: 5, packType: 'BOX', rate: 180.88, tradeDiscount: 3600, taxableValue: 16279.20 },
      { srNo: 7, description: 'ARZOL GALLOP 4T 10W/30 (API SN,JASO MA2) - (900 ML) - 1MR', hsnCode: '27101980', qty: 165, unit: 'LTR', packCount: 3, packType: 'CRB', rate: 130.30, tradeDiscount: 0, taxableValue: 21499.50 },
    ],
    totals: {
      taxableValue: 224173.40,
      cgst: 20175.63, sgst: 20175.63, igst: 0,
      freight: 0, roundOff: 0.34,
      grandTotal: 264525,
      amountInWords: 'INR Two Lakh Sixty Four Thousand Five Hundred Twenty Five Only',
    },
  };
}

/* -------------------------------------------------------------------- */
/*  Product resolve-or-create                                           */
/* -------------------------------------------------------------------- */

function makeProductCode(): string {
  const stamp = Date.now().toString(36).toUpperCase().slice(-6);
  const rnd = Math.random().toString(36).toUpperCase().slice(2, 4);
  return `PRD-${stamp}${rnd}`;
}

interface ResolvedProduct {
  productId?: string;
  code?: string;
  name?: string;
  hsnCode?: string;
  unit?: string;
  sellingPrice?: number;
  wasCreated: boolean;
}

async function resolveOrCreateProduct(line: ParsedLine): Promise<ResolvedProduct> {
  // 1. Try server-side filter; backend may ignore unknown params (G49)
  let candidates: Array<Record<string, unknown>> = [];
  try {
    candidates = (await productService.filter({
      // Cast to ProductFilter — the registered filter doesn't list hsnCode
      // yet (G49), but the wire layer forwards unknown keys to the query string.
      name: line.description.slice(0, 40),
    } as never)) as unknown as Array<Record<string, unknown>>;
  } catch {
    /* swallow — fallback to client filter below */
  }

  // 2. Client-side narrow by name prefix (live schema doesn't carry hsnCode
  //    today — see inspect-product-schema.mjs — so match on name only).
  const looksLikeMatch = (p: Record<string, unknown>) => {
    const name = String(p.name ?? '').toUpperCase();
    return name.startsWith(line.description.slice(0, 20).toUpperCase());
  };
  const hit = candidates.find(looksLikeMatch);
  if (hit) {
    return {
      productId: (hit.id as number | undefined)?.toString(),
      code: hit.code as string | undefined,
      name: hit.name as string | undefined,
      hsnCode: line.hsnCode,
      unit: (hit.unitOfMeasure as string | undefined) ?? line.unit,
      sellingPrice: (hit.price as number | undefined) ?? line.rate,
      wasCreated: false,
    };
  }

  // 3. Auto-create via /master-data/Product.
  //    Live schema (verified via inspect-product-schema.mjs):
  //      code, name, description, status, remarks, sku, barcode,
  //      categoryId, brandId, unitOfMeasure, price, mrp, cost,
  //      currency, taxRate, isService, isTrackStock, imageUrl.
  //    Note: the stale @kss/api `Product` TS type still describes the
  //    old `unit/sellingPrice/costPrice/isActive` shape; we send the
  //    live shape via `as never`.
  const code = makeProductCode();
  const created = await productService.create([
    {
      code,
      name: line.description,
      description: '',
      status: 0,
      remarks: '',
      sku: code,
      barcode: '',
      categoryId: null,
      brandId: null,
      unitOfMeasure: line.unit || 'PCS',
      price: line.rate,
      mrp: line.rate,
      cost: line.rate,
      currency: 'INR',
      taxRate: 18,
      isService: false,
      isTrackStock: true,
      imageUrl: '',
    } as never,
  ]);
  const row = (created[0] ?? {}) as unknown as Record<string, unknown>;
  return {
    productId: (row.id as number | undefined)?.toString(),
    code: (row.code as string | undefined) ?? code,
    name: line.description,
    hsnCode: line.hsnCode,
    unit: line.unit,
    sellingPrice: line.rate,
    wasCreated: true,
  };
}

/* -------------------------------------------------------------------- */
/*  Helpers                                                             */
/* -------------------------------------------------------------------- */

const inr = (n: number) =>
  n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ddMMyyyy = (iso: string) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

/* -------------------------------------------------------------------- */
/*  Page                                                                */
/* -------------------------------------------------------------------- */

export default function InvoiceIngest() {
  const nav = useNavigate();
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<ParsedInvoice | null>(null);
  const [kind, setKind] = useState<'SalesInvoice' | 'PurchaseInvoice'>('PurchaseInvoice');
  const [parsing, setParsing] = useState(false);
  const [posting, setPosting] = useState(false);
  const [log, setLog] = useState<string[]>([]);

  const totals = useMemo(() => parsed?.totals, [parsed]);

  /* ---------- handlers ---------- */

  const handleFile = (f: File | null) => {
    setFile(f);
    setParsed(null);
    setLog([]);
  };

  const onPickClick = () => inputRef.current?.click();

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f && f.type === 'application/pdf') handleFile(f);
    else toast.error('Drop a single PDF file');
  };

  const onParse = async () => {
    if (!file) {
      toast.error('Pick a PDF first');
      return;
    }
    setParsing(true);
    try {
      // TODO: real call when G48 lands
      //   const fd = new FormData(); fd.append('file', file);
      //   const r = await apiClient.post('/invoice-ingest/parse', fd);
      //   setParsed(r.data);
      const data = stubParseByFilename(file.name);
      setParsed(data);
      setKind(data.header.kindGuess === 'SalesOrder' ? 'SalesInvoice' : data.header.kindGuess);
      toast.success('Parsed (stub) — review and post');
    } catch (err) {
      toast.error(`Parse failed: ${(err as Error).message}`);
    } finally {
      setParsing(false);
    }
  };

  const updateLine = (i: number, patch: Partial<ParsedLine>) => {
    if (!parsed) return;
    const next = { ...parsed, lines: parsed.lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)) };
    // Recompute taxableValue when qty/rate/discount change
    next.lines = next.lines.map((l) => ({ ...l, taxableValue: round2(l.qty * l.rate - l.tradeDiscount) }));
    next.totals = recomputeTotals(next.lines, next.totals);
    setParsed(next);
  };

  const addLine = () => {
    if (!parsed) return;
    const srNo = parsed.lines.length + 1;
    setParsed({
      ...parsed,
      lines: [
        ...parsed.lines,
        { srNo, description: '', hsnCode: '', qty: 0, unit: 'NOS', packCount: 1, packType: '', rate: 0, tradeDiscount: 0, taxableValue: 0 },
      ],
    });
  };

  const removeLine = (i: number) => {
    if (!parsed) return;
    const lines = parsed.lines.filter((_, idx) => idx !== i).map((l, idx) => ({ ...l, srNo: idx + 1 }));
    setParsed({ ...parsed, lines, totals: recomputeTotals(lines, parsed.totals) });
  };

  /* ---------- post ---------- */

  const onPost = async () => {
    if (!parsed) return;
    setPosting(true);
    setLog([]);
    const push = (s: string) => setLog((prev) => [...prev, s]);
    try {
      push(`Resolving ${parsed.lines.length} product(s)…`);
      const resolved: ResolvedProduct[] = [];
      for (const l of parsed.lines) {
        const p = await resolveOrCreateProduct(l);
        push(p.wasCreated ? `  + created ${p.code} — ${l.description}` : `  ✓ matched ${p.code}`);
        resolved.push(p);
      }

      push('Creating invoice header…');
      const totalTax = parsed.totals.cgst + parsed.totals.sgst + parsed.totals.igst;
      const headerCode = `${kind === 'SalesInvoice' ? 'SI' : 'PI'}-${Date.now().toString(36).toUpperCase().slice(-6)}`;

      // Live schemas (inspect-invoice-schema.mjs):
      //   SalesInvoice    — customerId, customerName, paymentStatus, paymentMethod, invoiceStatus, notes
      //   PurchaseInvoice — supplierId, supplierName, purchaseOrderId, goodsReceiptNoteId
      const commonHeader = {
        code: headerCode,
        name: `${kind} ${parsed.header.docNumber}`,
        description: `Ingested from ${parsed.header.docNumber}`,
        status: 0,
        remarks: '',
        invoiceNumber: parsed.header.docNumber,
        invoiceDate: parsed.header.docDate,
        dueDate: parsed.header.dueDate ?? null,
        currency: 'INR',
        subTotal: parsed.totals.taxableValue,
        discountAmount: 0,
        taxAmount: totalTax,
        grandTotal: parsed.totals.grandTotal,
        paidAmount: 0,
        balance: parsed.totals.grandTotal,
        paymentStatus: 'Unpaid',
        invoiceStatus: 'Draft',
      };
      const headerPayload =
        kind === 'SalesInvoice'
          ? {
              ...commonHeader,
              customerId: null,
              customerName: parsed.header.partyName,
              roundingAmount: parsed.totals.roundOff,
              paymentMethod: '',
              notes: `GSTIN ${parsed.header.partyGstin} · ${parsed.header.partyAddress}`,
            }
          : {
              ...commonHeader,
              supplierId: 0,
              supplierName: parsed.header.partyName,
              purchaseOrderId: null,
              goodsReceiptNoteId: null,
            };

      const created =
        kind === 'SalesInvoice'
          ? await salesInvoiceService.create([headerPayload as never])
          : await purchaseInvoiceService.create([headerPayload as never]);
      const header = (created[0] ?? {}) as unknown as Record<string, unknown>;
      const headerNumericId = header.id as number | undefined;
      const headerCodeReturned = (header.code as string | undefined) ?? headerCode;
      push(`  → header ${headerCodeReturned} (id=${headerNumericId ?? '?'})`);

      push('Creating line items…');
      for (let i = 0; i < parsed.lines.length; i++) {
        const l = parsed.lines[i]!;
        const p = resolved[i]!;
        const lineCode = `${headerCode}-L${l.srNo}`;
        const productNumericId = p.productId ? Number(p.productId) : null;

        // Live SalesInvoiceLine fields:
        //   code, name, description, status, remarks, salesInvoiceId,
        //   lineNumber, productId, productCode, productName, sku,
        //   quantity, unitOfMeasure, unitPrice, lineDiscount,
        //   taxableAmount, taxRate, taxAmount, lineTotal
        // Live PurchaseInvoiceLine fields (slightly trimmer):
        //   code, name, description, status, remarks, purchaseInvoiceId,
        //   lineNumber, productId, productName, quantity, unitOfMeasure,
        //   unitPrice, taxRate, taxAmount, lineTotal
        const commonLine = {
          code: lineCode,
          name: l.description.slice(0, 50),
          description: '',
          status: 0,
          remarks: '',
          lineNumber: l.srNo,
          productId: productNumericId,
          productName: l.description,
          quantity: l.qty,
          unitOfMeasure: l.unit || 'PCS',
          unitPrice: l.rate,
          taxRate: 18,
          taxAmount: round2(l.taxableValue * 0.18),
          lineTotal: round2(l.taxableValue * 1.18),
        };
        if (kind === 'SalesInvoice') {
          await salesInvoiceLineService.create([
            {
              ...commonLine,
              salesInvoiceId: headerNumericId ?? 0,
              productCode: p.code ?? '',
              sku: p.code ?? '',
              lineDiscount: l.tradeDiscount,
              taxableAmount: l.taxableValue,
            } as never,
          ]);
        } else {
          await purchaseInvoiceLineService.create([
            {
              ...commonLine,
              purchaseInvoiceId: headerNumericId ?? 0,
            } as never,
          ]);
        }
      }
      push(`  → ${parsed.lines.length} line(s) posted`);

      push('Writing inventory movements…');
      // Live InventoryMovement fields:
      //   code, name, description, status, remarks, productId,
      //   warehouseId, movementType, direction, quantity,
      //   unitOfMeasure, unitCost, occurredAt, sourceType, sourceId,
      //   batchNumber
      for (let i = 0; i < parsed.lines.length; i++) {
        const l = parsed.lines[i]!;
        const p = resolved[i]!;
        const mv = {
          code: `INVM-${Date.now().toString(36).toUpperCase().slice(-6)}-${i + 1}`,
          name: `${kind === 'PurchaseInvoice' ? 'IN' : 'OUT'} · ${p.code} · ${l.qty} ${l.unit}`,
          description: `${kind} ${headerCodeReturned} line ${l.srNo}`,
          status: 0,
          remarks: '',
          productId: p.productId ? Number(p.productId) : 0,
          warehouseId: null,
          movementType: kind === 'PurchaseInvoice' ? 'Receipt' : 'Issue',
          direction: kind === 'PurchaseInvoice' ? 'In' : 'Out',
          quantity: l.qty,
          unitOfMeasure: l.unit || 'PCS',
          unitCost: l.rate,
          occurredAt: new Date(parsed.header.docDate).toISOString(),
          sourceType: kind,
          sourceId: headerNumericId ?? null,
          batchNumber: '',
        };
        await inventoryMovementService.create([mv as never]);
      }
      push(`  → ${parsed.lines.length} movement(s) posted`);

      push(`✅ Done. Invoice ${headerCode} created.`);
      toast.success(`Posted ${kind} ${headerCode}`);
      // Stay on the page so the operator sees the log; offer nav button
    } catch (err) {
      push(`❌ ${(err as Error).message}`);
      toast.error(`Post failed: ${(err as Error).message}`);
    } finally {
      setPosting(false);
    }
  };

  /* ---------- render ---------- */

  return (
    <div className="space-y-4 p-4">
      {/* ── Toolbar ───────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" onClick={() => nav(-1)}>
          <ArrowLeft size={16} className="mr-1" />
          Back
        </Button>
        <h1 className="text-lg font-semibold">Upload Tax Invoice</h1>
        <div className="ml-auto flex items-center gap-2">
          <label className="text-xs text-content-muted">Post as</label>
          <div className="inline-flex rounded-md border border-border">
            <button
              type="button"
              onClick={() => setKind('PurchaseInvoice')}
              className={`px-3 py-1.5 text-xs ${kind === 'PurchaseInvoice' ? 'bg-primary text-white' : 'text-content-primary'}`}
            >
              Purchase (stock IN)
            </button>
            <button
              type="button"
              onClick={() => setKind('SalesInvoice')}
              className={`px-3 py-1.5 text-xs ${kind === 'SalesInvoice' ? 'bg-primary text-white' : 'text-content-primary'}`}
            >
              Sales (stock OUT)
            </button>
          </div>
        </div>
      </div>

      {/* ── Upload zone ───────────────────────────────────────────── */}
      <Card>
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDrop}
          className="flex flex-col items-center gap-2 rounded-md border-2 border-dashed border-border p-6 text-center"
        >
          <Upload size={32} className="text-content-muted" />
          <div className="text-sm">
            {file ? (
              <>
                <FileText className="inline" size={16} /> <b>{file.name}</b>{' '}
                <span className="text-content-muted">({(file.size / 1024).toFixed(0)} KB)</span>
              </>
            ) : (
              'Drag-and-drop a tax-invoice PDF here, or click to browse'
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
          />
          <div className="mt-2 flex gap-2">
            <Button variant="outline" size="sm" onClick={onPickClick}>Browse</Button>
            <Button onClick={onParse} disabled={!file || parsing} size="sm">
              {parsing ? <Loader2 className="mr-1 animate-spin" size={14} /> : null}
              Parse PDF
            </Button>
          </div>
          <p className="mt-1 text-[11px] text-content-muted">
            Backend parser (§36.1) is pending — until it ships, parsing uses a client-side stub
            matching the two reference PDFs.
          </p>
        </div>
      </Card>

      {/* ── Review + Preview ─────────────────────────────────────── */}
      {parsed && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {/* LEFT — Editable parsed data */}
          <Card>
            <div className="space-y-3 p-3">
              <h2 className="text-sm font-semibold">Parsed data (review &amp; edit)</h2>
              <HeaderEditor
                header={parsed.header}
                onChange={(h) => setParsed({ ...parsed, header: h })}
              />
              <LineTableEditor
                lines={parsed.lines}
                onUpdate={updateLine}
                onAdd={addLine}
                onRemove={removeLine}
              />
              <TotalsEditor totals={parsed.totals} onChange={(t) => setParsed({ ...parsed, totals: t })} />
            </div>
          </Card>

          {/* RIGHT — Tax-invoice-style preview */}
          <Card>
            <InvoicePreview parsed={parsed} kind={kind} />
          </Card>
        </div>
      )}

      {/* ── Post + log ────────────────────────────────────────────── */}
      {parsed && (
        <div className="flex flex-col gap-3">
          <div className="flex justify-end">
            <Button onClick={onPost} disabled={posting} size="md">
              {posting ? <Loader2 className="mr-1 animate-spin" size={14} /> : <Save className="mr-1" size={14} />}
              Post {kind === 'SalesInvoice' ? 'Sales' : 'Purchase'} Invoice
            </Button>
          </div>
          {log.length > 0 && (
            <Card>
              <pre className="whitespace-pre-wrap p-3 text-xs leading-5 text-content-primary">
                {log.join('\n')}
              </pre>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

/* ==================================================================== */
/*  Sub-components                                                       */
/* ==================================================================== */

function HeaderEditor({
  header,
  onChange,
}: {
  header: ParsedHeader;
  onChange: (h: ParsedHeader) => void;
}) {
  const Field = ({ label, k }: { label: string; k: keyof ParsedHeader }) => (
    <label className="flex flex-col text-xs">
      <span className="text-content-muted">{label}</span>
      <input
        type="text"
        value={String(header[k] ?? '')}
        onChange={(e) => onChange({ ...header, [k]: e.target.value })}
        className="h-8 rounded border border-border bg-surface-card px-2 text-sm"
      />
    </label>
  );
  return (
    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 lg:grid-cols-3">
      <Field label="Doc No" k="docNumber" />
      <Field label="Doc Date" k="docDate" />
      <Field label="Due Date" k="dueDate" />
      <Field label="Party Name" k="partyName" />
      <Field label="Party GSTIN" k="partyGstin" />
      <Field label="State" k="partyState" />
      <Field label="State Code" k="partyStateCode" />
      <Field label="Contact" k="partyContact" />
      <Field label="Phone" k="partyPhone" />
      <div className="md:col-span-2 lg:col-span-3">
        <Field label="Address" k="partyAddress" />
      </div>
    </div>
  );
}

function LineTableEditor({
  lines,
  onUpdate,
  onAdd,
  onRemove,
}: {
  lines: ParsedLine[];
  onUpdate: (i: number, patch: Partial<ParsedLine>) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-xs">
        <thead className="bg-surface-muted text-left">
          <tr>
            <th className="px-2 py-1.5">Sr</th>
            <th className="px-2 py-1.5">Description</th>
            <th className="px-2 py-1.5">HSN</th>
            <th className="px-2 py-1.5">Qty</th>
            <th className="px-2 py-1.5">Unit</th>
            <th className="px-2 py-1.5">Packs</th>
            <th className="px-2 py-1.5">Type</th>
            <th className="px-2 py-1.5 text-right">Rate</th>
            <th className="px-2 py-1.5 text-right">Trade Dis</th>
            <th className="px-2 py-1.5 text-right">Taxable</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-t border-border">
              <td className="px-2 py-1">{l.srNo}</td>
              <td className="px-2 py-1">
                <input
                  value={l.description}
                  onChange={(e) => onUpdate(i, { description: e.target.value })}
                  className="h-7 w-56 rounded border border-border bg-surface-card px-1.5"
                />
              </td>
              <td className="px-2 py-1">
                <input
                  value={l.hsnCode}
                  onChange={(e) => onUpdate(i, { hsnCode: e.target.value })}
                  className="h-7 w-20 rounded border border-border bg-surface-card px-1.5"
                />
              </td>
              <td className="px-2 py-1">
                <input
                  type="number"
                  value={l.qty}
                  onChange={(e) => onUpdate(i, { qty: +e.target.value })}
                  className="h-7 w-16 rounded border border-border bg-surface-card px-1.5 text-right"
                />
              </td>
              <td className="px-2 py-1">
                <input
                  value={l.unit}
                  onChange={(e) => onUpdate(i, { unit: e.target.value })}
                  className="h-7 w-14 rounded border border-border bg-surface-card px-1.5"
                />
              </td>
              <td className="px-2 py-1">
                <input
                  type="number"
                  value={l.packCount}
                  onChange={(e) => onUpdate(i, { packCount: +e.target.value })}
                  className="h-7 w-12 rounded border border-border bg-surface-card px-1.5 text-right"
                />
              </td>
              <td className="px-2 py-1">
                <input
                  value={l.packType}
                  onChange={(e) => onUpdate(i, { packType: e.target.value })}
                  className="h-7 w-14 rounded border border-border bg-surface-card px-1.5"
                />
              </td>
              <td className="px-2 py-1">
                <input
                  type="number"
                  value={l.rate}
                  onChange={(e) => onUpdate(i, { rate: +e.target.value })}
                  className="h-7 w-20 rounded border border-border bg-surface-card px-1.5 text-right"
                />
              </td>
              <td className="px-2 py-1">
                <input
                  type="number"
                  value={l.tradeDiscount}
                  onChange={(e) => onUpdate(i, { tradeDiscount: +e.target.value })}
                  className="h-7 w-20 rounded border border-border bg-surface-card px-1.5 text-right"
                />
              </td>
              <td className="px-2 py-1 text-right tabular-nums">{inr(l.taxableValue)}</td>
              <td className="px-2 py-1">
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  className="text-content-muted hover:text-red-600"
                  title="Remove line"
                >
                  <Trash2 size={14} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Button size="sm" variant="outline" onClick={onAdd} className="mt-2">
        <Plus size={14} className="mr-1" />
        Add Line
      </Button>
    </div>
  );
}

function TotalsEditor({
  totals,
  onChange,
}: {
  totals: ParsedTotals;
  onChange: (t: ParsedTotals) => void;
}) {
  const Field = ({ label, k }: { label: string; k: keyof ParsedTotals }) => (
    <label className="flex flex-col text-xs">
      <span className="text-content-muted">{label}</span>
      <input
        type={typeof totals[k] === 'number' ? 'number' : 'text'}
        value={String(totals[k] ?? '')}
        onChange={(e) =>
          onChange({ ...totals, [k]: typeof totals[k] === 'number' ? +e.target.value : e.target.value })
        }
        className="h-8 rounded border border-border bg-surface-card px-2 text-right text-sm tabular-nums"
      />
    </label>
  );
  return (
    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
      <Field label="Taxable Value" k="taxableValue" />
      <Field label="CGST" k="cgst" />
      <Field label="SGST" k="sgst" />
      <Field label="IGST" k="igst" />
      <Field label="Freight" k="freight" />
      <Field label="Round Off" k="roundOff" />
      <Field label="Grand Total" k="grandTotal" />
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Right-pane preview — visually matches the source tax invoice        */
/* -------------------------------------------------------------------- */

function InvoicePreview({ parsed, kind }: { parsed: ParsedInvoice; kind: string }) {
  const h = parsed.header;
  const t = parsed.totals;
  return (
    <div className="p-3 text-[11px] leading-snug text-content-primary">
      <div className="border border-black">
        <div className="border-b border-black bg-surface-muted px-2 py-1 text-center font-semibold">
          {kind === 'SalesInvoice' ? 'TAX INVOICE' : kind === 'PurchaseInvoice' ? 'PURCHASE INVOICE' : 'DOMESTIC SALES ORDER'}
        </div>

        {/* Doc + transport block */}
        <div className="grid grid-cols-2 border-b border-black">
          <div className="border-r border-black p-2">
            <div><b>Invoice No.</b> : {h.docNumber}</div>
            <div><b>Date</b> : {ddMMyyyy(h.docDate)}</div>
            <div><b>State</b> : {h.partyState} <b>Code</b>: {h.partyStateCode}</div>
            {h.irn && <div className="break-all"><b>IRN</b> : {h.irn}</div>}
          </div>
          <div className="p-2">
            <div><b>Transport</b> : {h.transportName}</div>
            <div><b>E-way Bill</b> : {h.ewayBillNo || '—'}</div>
            <div><b>Due On</b> : {h.dueDate ? ddMMyyyy(h.dueDate) : '—'}</div>
          </div>
        </div>

        {/* Receiver / Consignee */}
        <div className="grid grid-cols-2 border-b border-black">
          <div className="border-r border-black p-2">
            <div className="mb-1 font-semibold">Details Of Receiver (Billed To)</div>
            <div><b>{h.partyName}</b></div>
            <div>{h.partyAddress}</div>
            <div>State: {h.partyState} · Code: {h.partyStateCode}</div>
            <div>GSTIN: {h.partyGstin}</div>
            <div>Email: {h.partyEmail} · Phone: {h.partyPhone}</div>
            <div>Contact: {h.partyContact}</div>
          </div>
          <div className="p-2">
            <div className="mb-1 font-semibold">Details Of Consignee (Shipped To)</div>
            <div><b>{h.partyName}</b></div>
            <div>{h.partyAddress}</div>
            <div>GSTIN: {h.partyGstin}</div>
          </div>
        </div>

        {/* Lines */}
        <table className="w-full border-b border-black">
          <thead className="bg-surface-muted">
            <tr>
              <th className="border-r border-black px-1.5 py-1 text-left">Sr</th>
              <th className="border-r border-black px-1.5 py-1 text-left">Description</th>
              <th className="border-r border-black px-1.5 py-1">HSN/SAC</th>
              <th className="border-r border-black px-1.5 py-1 text-right">Qty</th>
              <th className="border-r border-black px-1.5 py-1">Unit</th>
              <th className="border-r border-black px-1.5 py-1 text-right">Packs</th>
              <th className="border-r border-black px-1.5 py-1">Type</th>
              <th className="border-r border-black px-1.5 py-1 text-right">Rate</th>
              <th className="border-r border-black px-1.5 py-1 text-right">Tr Dis</th>
              <th className="px-1.5 py-1 text-right">Taxable</th>
            </tr>
          </thead>
          <tbody>
            {parsed.lines.map((l) => (
              <tr key={l.srNo} className="border-t border-black">
                <td className="border-r border-black px-1.5 py-0.5">{l.srNo}</td>
                <td className="border-r border-black px-1.5 py-0.5">{l.description}</td>
                <td className="border-r border-black px-1.5 py-0.5 text-center">{l.hsnCode}</td>
                <td className="border-r border-black px-1.5 py-0.5 text-right tabular-nums">{l.qty.toFixed(2)}</td>
                <td className="border-r border-black px-1.5 py-0.5 text-center">{l.unit}</td>
                <td className="border-r border-black px-1.5 py-0.5 text-right">{l.packCount}</td>
                <td className="border-r border-black px-1.5 py-0.5 text-center">{l.packType}</td>
                <td className="border-r border-black px-1.5 py-0.5 text-right tabular-nums">{inr(l.rate)}</td>
                <td className="border-r border-black px-1.5 py-0.5 text-right tabular-nums">{l.tradeDiscount ? inr(l.tradeDiscount) : '—'}</td>
                <td className="px-1.5 py-0.5 text-right tabular-nums">{inr(l.taxableValue)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Totals */}
        <div className="grid grid-cols-2">
          <div className="border-r border-black p-2">
            <div className="text-[10px] uppercase text-content-muted">Amount in words</div>
            <div className="italic">{t.amountInWords}</div>
            <div className="mt-2 text-[10px] uppercase text-content-muted">Bank</div>
            <div>A/C: ARABIAN PETROLEUM LTD — BHIWANDI</div>
            <div>IFSC: KKBK0000000  ·  A/C No.: 7712056011</div>
          </div>
          <div className="p-2 text-right tabular-nums">
            <Row label="Taxable Value" value={inr(t.taxableValue)} />
            <Row label="CGST" value={inr(t.cgst)} />
            <Row label="SGST" value={inr(t.sgst)} />
            <Row label="IGST" value={inr(t.igst)} />
            <Row label="Freight" value={inr(t.freight)} />
            <Row label="Round Off" value={inr(t.roundOff)} />
            <div className="mt-1 border-t border-black pt-1 font-semibold">
              <Row label="Invoice Total" value={inr(t.grandTotal)} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

/* -------------------------------------------------------------------- */
/*  Maths                                                               */
/* -------------------------------------------------------------------- */

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function recomputeTotals(lines: ParsedLine[], prev: ParsedTotals): ParsedTotals {
  const taxableValue = round2(lines.reduce((s, l) => s + l.taxableValue, 0));
  // Keep the originally-parsed tax shares but rebase to the new taxable value
  const ratio = prev.taxableValue ? taxableValue / prev.taxableValue : 1;
  const cgst = round2(prev.cgst * ratio);
  const sgst = round2(prev.sgst * ratio);
  const igst = round2(prev.igst * ratio);
  const grandTotal = round2(taxableValue + cgst + sgst + igst + prev.freight + prev.roundOff);
  return { ...prev, taxableValue, cgst, sgst, igst, grandTotal };
}
